import React from 'react';
import { Check, X } from 'lucide-react';
import { motion } from 'framer-motion';
const Section = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6
}} className={`max-w-3xl mx-auto py-12 px-4 ${className}`} data-id="element-90">
    {children}
  </motion.div>;
export function SalesCopy() {
  return <div className="space-y-8 text-lg text-gray-700 leading-relaxed" data-id="element-91">
      {/* Intro Problem */}
      <Section data-id="element-92">
        <p className="mb-6" data-id="element-93">
          Vivimos creyendo que el problema es la falta de voluntad. Que si no
          mantenemos una rutina es porque "no somos constantes".
        </p>
        <p className="mb-6 font-semibold text-xl" data-id="element-94">Pero la verdad es otra.</p>
        <p className="mb-6" data-id="element-95">
          El problema no sos vos. El problema es que intentaste cuidarte sin
          sistema.
        </p>
      </Section>

      {/* The Fatigue */}
      <Section className="bg-white rounded-3xl shadow-sm p-8 md:p-12 my-8 border border-[#E8D5D5]" data-id="element-96">
        <h2 className="text-3xl font-serif font-bold text-[#2D3436] mb-6" data-id="element-97">
          El cansancio que nadie ve
        </h2>
        <p className="mb-4" data-id="element-98">
          Te levantás con el cuerpo duro. La espalda tensa. La cabeza llena.
        </p>
        <p className="mb-4" data-id="element-99">
          Funcionás. Cumplís. Avanzás. Pero siempre cansada.
        </p>
        <p className="mb-6" data-id="element-100">
          Y en algún punto empezás a pensar que eso es "normal".
        </p>
        <p className="font-bold text-[#9CAF88] text-xl italic" data-id="element-101">No lo es.</p>
      </Section>

      {/* Tried Everything */}
      <Section data-id="element-102">
        <h2 className="text-3xl font-serif font-bold text-[#2D3436] mb-6" data-id="element-103">
          Probaste de todo… y nada se sostuvo
        </h2>
        <ul className="space-y-4 mb-8" data-id="element-104">
          <li className="flex items-start gap-3" data-id="element-105">
            <X className="text-red-400 mt-1 flex-shrink-0" data-id="element-106" />
            <span data-id="element-107">Apps que te entusiasman una semana.</span>
          </li>
          <li className="flex items-start gap-3" data-id="element-108">
            <X className="text-red-400 mt-1 flex-shrink-0" data-id="element-109" />
            <span data-id="element-110">Videos sueltos que no sabés cómo combinar.</span>
          </li>
          <li className="flex items-start gap-3" data-id="element-111">
            <X className="text-red-400 mt-1 flex-shrink-0" data-id="element-112" />
            <span data-id="element-113">Gimnasios que no encajan con tu vida.</span>
          </li>
        </ul>
        <p className="mb-4" data-id="element-114">
          Y cada vez que abandonás, algo se rompe un poco más:
        </p>
        <p className="font-bold text-xl text-[#2D3436]" data-id="element-115">Tu confianza.</p>
      </Section>

      {/* The Solution */}
      <Section className="bg-[#9CAF88]/10 rounded-3xl p-8 md:p-12 text-center" data-id="element-116">
        <h2 className="text-3xl font-serif font-bold text-[#2D3436] mb-4" data-id="element-117">
          No necesitás más motivación
        </h2>
        <h3 className="text-4xl font-serif font-bold text-[#9CAF88] mb-8" data-id="element-118">
          Necesitás estructura
        </h3>
        <p className="mb-6" data-id="element-119">
          Eso fue lo que entendí después de años de probar "todo".
        </p>
        <p className="font-medium text-xl" data-id="element-120">
          No necesitaba más ganas. Necesitaba un sistema que me sostenga incluso
          cuando no tengo ganas.
        </p>
        <p className="mt-8 text-2xl font-serif italic" data-id="element-121">
          Así nació el Sistema Integral Yoga Esencial.
        </p>
      </Section>

      {/* What it is */}
      <Section data-id="element-122">
        <h2 className="text-3xl font-serif font-bold text-[#2D3436] mb-6" data-id="element-123">
          Qué es realmente Yoga Esencial
        </h2>
        <p className="mb-6" data-id="element-124">No es un curso. No es una app. No es un PDF más.</p>
        <p className="mb-8 font-medium" data-id="element-125">
          Es un camino completo, pensado para mujeres reales, con vidas reales.
        </p>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-126">
          {['Práctica progresiva', 'Comprensión corporal', 'Rutinas diarias realistas', 'Recuperación terapéutica', 'Registro y seguimiento', 'Formación avanzada'].map((item, i) => <div key={i} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100" data-id="element-127">
              <div className="bg-[#9CAF88] text-white p-1 rounded-full" data-id="element-128">
                <Check size={16} data-id="element-129" />
              </div>
              <span className="font-medium" data-id="element-130">{item}</span>
            </div>)}
        </div>
        <p className="mt-6 text-center text-gray-500 italic" data-id="element-131">
          Todo ordenado. Todo conectado.
        </p>
      </Section>

      {/* How it works */}
      <Section className="bg-white rounded-3xl shadow-lg p-8 md:p-12 border-t-4 border-[#9CAF88]" data-id="element-132">
        <h2 className="text-3xl font-serif font-bold text-[#2D3436] mb-8 text-center" data-id="element-133">
          Cómo funciona el sistema
        </h2>
        <p className="text-center mb-8" data-id="element-134">
          No empezás forzando. Empezás entendiendo.
        </p>

        <div className="space-y-4" data-id="element-135">
          {['Movés el cuerpo sin dolor', 'Fortalecés con seguridad', 'Comprendés lo que hacés', 'Creás hábito', 'Profundizás', 'Dominás tu práctica'].map((step, i) => <div key={i} className="flex items-center gap-4" data-id="element-136">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E8D5D5] text-[#2D3436] flex items-center justify-center font-bold font-serif" data-id="element-137">
                {i + 1}
              </div>
              <span className="text-lg" data-id="element-138">{step}</span>
            </div>)}
        </div>
        <p className="mt-8 text-center font-bold text-[#9CAF88] text-xl" data-id="element-139">
          Paso a paso. Sin presión.
        </p>
      </Section>

      {/* 90 Days Promise */}
      <Section data-id="element-140">
        <h2 className="text-3xl font-serif font-bold text-[#2D3436] mb-8 text-center" data-id="element-141">
          En 90 días podés…
        </h2>
        <div className="grid gap-4" data-id="element-142">
          {['Despertarte con menos dolor', 'Sentirte más liviana', 'Respirar mejor', 'Tener una rutina que sí cumplís', 'Volver a confiar en vos'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-143">
              <Check className="text-[#9CAF88] flex-shrink-0" size={24} data-id="element-144" />
              <span className="text-lg font-medium" data-id="element-145">{item}</span>
            </div>)}
        </div>
        <p className="mt-8 text-center italic text-gray-500" data-id="element-146">
          No por magia. Por método.
        </p>
      </Section>

      {/* Final Pitch */}
      <Section className="text-center" data-id="element-147">
        <h2 className="text-3xl font-serif font-bold text-[#2D3436] mb-4" data-id="element-148">
          Este no es otro intento
        </h2>
        <p className="text-xl mb-4" data-id="element-149">Es una base.</p>
        <p className="mb-6" data-id="element-150">
          Una forma distinta de relacionarte con tu cuerpo, tu tiempo y tu
          disciplina.
        </p>
        <p className="font-bold text-xl text-[#9CAF88] mb-8" data-id="element-151">
          Sin culpa. Sin exigencia. Sin humo. Con estructura.
        </p>
      </Section>
    </div>;
}